import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { api } from "../lib/api";
import { Minus, Plus, X, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function Cart() {
    const { items, remove, updateQty, subtotal, clear } = useCart();
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);

    const checkout = async () => {
        if (items.length === 0) return;
        setLoading(true);
        try {
            const { data } = await api.post("/checkout/session", {
                items: items.map((x) => ({ price_id: x.price_id, quantity: x.quantity })),
                origin_url: window.location.origin,
                customer_email: email || undefined,
            });
            if (data.url) {
                window.location.href = data.url;
            }
        } catch (e) {
            toast.error("Could not start checkout. Please try again.");
            setLoading(false);
        }
    };

    if (items.length === 0) {
        return (
            <div data-testid="cart-empty" className="px-6 py-32 max-w-3xl mx-auto text-center">
                <div className="eyebrow text-[#B8860B] mb-4">The Cart</div>
                <h1 className="font-serif text-5xl md:text-6xl tracking-tight mb-6">Quiet here.</h1>
                <p className="text-black/65 mb-10 font-light">Nothing in your cart yet. Take a look around the shop.</p>
                <Link to="/shop" className="kind-btn-primary">Visit the Shop <ArrowRight size={16} /></Link>
            </div>
        );
    }

    return (
        <div data-testid="cart-page" className="px-6 md:px-12 lg:px-20 py-16 md:py-20 max-w-6xl mx-auto">
            <div className="eyebrow text-[#B8860B] mb-4">The Cart</div>
            <h1 className="font-serif text-5xl md:text-6xl tracking-tight mb-12">Your Items</h1>

            <div className="grid md:grid-cols-12 gap-12">
                <div className="md:col-span-7 space-y-8">
                    {items.map((it) => (
                        <div key={it.price_id} data-testid={`cart-item-${it.price_id}`} className="flex gap-6 pb-8 border-b border-black/10">
                            <div className="w-24 h-24 bg-[#F4F4F4] flex-shrink-0">
                                {it.image_url ? (
                                    <img src={it.image_url} alt={it.name} className="w-full h-full object-cover" />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center text-[#B8860B] font-serif text-2xl">
                                        {it.name?.[0]}
                                    </div>
                                )}
                            </div>
                            <div className="flex-1">
                                <h3 className="font-serif text-xl tracking-tight mb-2">{it.name}</h3>
                                <div className="font-sans tabular-nums mb-3">${it.price.toFixed(2)}</div>
                                <div className="flex items-center border border-black/20 w-fit">
                                    <button onClick={() => updateQty(it.price_id, it.quantity - 1)} className="p-2 hover:bg-black hover:text-white">
                                        <Minus size={14} />
                                    </button>
                                    <span className="px-4 tabular-nums text-sm">{it.quantity}</span>
                                    <button onClick={() => updateQty(it.price_id, it.quantity + 1)} className="p-2 hover:bg-black hover:text-white">
                                        <Plus size={14} />
                                    </button>
                                </div>
                            </div>
                            <button onClick={() => remove(it.price_id)} data-testid={`remove-${it.price_id}`} className="text-black/40 hover:text-black self-start">
                                <X size={18} />
                            </button>
                        </div>
                    ))}
                </div>

                <div className="md:col-span-5 md:col-start-8">
                    <div className="bg-[#FAFAFA] p-8 border border-black/10">
                        <h3 className="font-serif text-2xl mb-6">Order Summary</h3>
                        <div className="flex justify-between mb-4 text-sm">
                            <span>Subtotal</span><span className="tabular-nums" data-testid="cart-subtotal">${subtotal.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between mb-6 text-sm text-black/60">
                            <span>Shipping</span><span>Calculated at checkout</span>
                        </div>
                        <div className="border-t border-black/15 pt-4 flex justify-between mb-6">
                            <span className="font-serif text-lg">Total</span>
                            <span className="font-serif text-lg tabular-nums">${subtotal.toFixed(2)}</span>
                        </div>

                        <label className="block eyebrow text-black/60 mb-2 text-[10px]">Email (optional)</label>
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="you@example.com"
                            data-testid="cart-email"
                            className="w-full border-b border-black/30 focus:border-[#D4AF37] outline-none py-2 bg-transparent text-sm mb-6"
                        />

                        <button onClick={checkout} disabled={loading} data-testid="checkout-button" className="kind-btn-gold w-full justify-center disabled:opacity-60">
                            {loading ? "Redirecting…" : "Checkout"} <ArrowRight size={16} />
                        </button>
                        <button onClick={clear} className="text-xs text-black/40 hover:text-black mt-4 underline">Clear cart</button>
                    </div>
                </div>
            </div>
        </div>
    );
}
